import { Select, SelectOption } from '@bloomfieldrobotics/canopy';
import { useLayoutEffect, useMemo } from 'react';
import { useRanchesByCustomer } from '../../../services/entities/hooks/useRanchesByCustomer';
import { Customer } from '../../../services/entities/impl/blfApi/schemas/customer';
import { Ranch } from '../../../services/entities/impl/blfApi/schemas/ranch';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { existsInItems } from './utils/existsInItems';
type RanchDropdownProps = {
  customer: Customer['id'];
  value: string | undefined;
  onChange: (value: string) => void;
  fullWidth?: boolean;
  disabled?: boolean;
  extraOptions?: SelectOption<string>[];
};

export const RanchDropdown = ({
  customer,
  value,
  onChange,
  fullWidth = false,
  disabled = false,
  extraOptions = [],
}: RanchDropdownProps) => {
  const { t } = useTranslation();

  const { ranches, isRanchesLoading } = useRanchesByCustomer(customer, {
    skip: customer === 'ALL_CUSTOMERS',
  });

  const ranchDropdownItems = useMemo(() => {
    const hasExtraOptions = extraOptions?.length > 0;

    if (ranches) {
      const ranchOptions = ranches
        .map((ranch) => {
          if (existsInItems<Ranch>(ranch.id, ranches)) {
            return {
              label: ranch.name,
              value: ranch.id,
            };
          }
        })
        .filter(Boolean) as SelectOption<string>[];

      // Check if extra options are provided and append them to the block options
      if (hasExtraOptions) {
        return [...extraOptions, ...ranchOptions];
      }

      return ranchOptions;
    }

    return hasExtraOptions ? extraOptions : [];
  }, [ranches, extraOptions]);

  // Auto-select single items and validate selections
  useLayoutEffect(() => {
    if (ranches?.length === 1 && ranches[0]) {
      const singleRanchId = ranches[0].id;
      onChange(singleRanchId);
    }
  }, [ranches, onChange]);

  return (
    <Select
      options={ranchDropdownItems}
      fullWidth={fullWidth}
      value={isRanchesLoading ? '' : value}
      onChange={onChange}
      label={t('Ranch')}
      loading={isRanchesLoading}
      disabled={!customer || disabled}
    />
  );
};
